import axios, { AxiosResponse as axiosResponse, ResponseType as responseType } from 'axios';

import { JWT_CACHE_KEY } from 'shared/consts';
import { LocalStorage } from 'tools/LocalStorage/LocalStorage';

import { baseUrl, metricsBaseUrl } from './baseUrl';

type LogoutCallback = () => void;

let logoutCallback: LogoutCallback | null = null;

const scheduleLogout = () => {
  if (!logoutCallback) {
    console.warn('Received 401 response but logout handler is not registered');
    return;
  }

  try {
    logoutCallback();
  } catch (error) {
    console.error('Failed to dispatch logout after 401 response', error);
  }
};

export const registerLogoutCallback = (callback: LogoutCallback) => {
  logoutCallback = callback;
};

const axiosInstance = axios.create({ baseURL: baseUrl });

const axiosInstanceWithToken = axios.create({ baseURL: baseUrl });
axiosInstanceWithToken.interceptors.request.use(config => {
  const token = LocalStorage.get<string>(JWT_CACHE_KEY);

  config.headers = {
    ...config.headers,
    Authorization: token ? `Bearer ${token}` : '',
  };

  return config;
});

axiosInstanceWithToken.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 401 && error?.config?.url !== 'api/auth/sessions') {
      scheduleLogout();
    }
    throw error;
  },
);

const metricsAxiosInstance = axios.create({ baseURL: metricsBaseUrl });
metricsAxiosInstance.interceptors.request.use(config => {
  const token = LocalStorage.get<string>(JWT_CACHE_KEY);

  config.headers = {
    ...config.headers,
    Authorization: token ? `Bearer ${token}` : '',
  };

  return config;
});

metricsAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 401 && error?.config?.url !== 'api/auth/sessions') {
      scheduleLogout();
    }
    throw error;
  },
);

const proxyAxiosInstance = axios.create({ baseURL: baseUrl });
proxyAxiosInstance.interceptors.request.use(config => {
  const token = LocalStorage.get<string>(JWT_CACHE_KEY);

  config.headers = {
    ...config.headers,
    Authorization: token ? `Bearer ${token}` : '',
  };

  return config;
});

proxyAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status === 401) {
      scheduleLogout();
    }
    throw error;
  },
);

export type ResponseType = responseType;
export type AxiosResponse<T> = axiosResponse<T>;
export {
  axiosInstance as axios,
  axiosInstanceWithToken as axiosToken,
  metricsAxiosInstance as metricsAxios,
  proxyAxiosInstance as proxyAxios,
};
